import { animated, useSpring } from '@react-spring/web';
import { useDrag } from '@use-gesture/react';
import { useCallback, useEffect, useRef, useState, type JSX } from 'react';
import {
  DRAG_CONFIG,
  FULL_HEIGHT_PERCENTAGE,
  INITIAL_HEIGHT_PERCENTAGE,
  SPRING_CONFIG,
  TAP_THRESHOLD,
} from './CollapsibleBottomSheet.constants';
import styles from './CollapsibleBottomSheet.module.css';
import type { CollapsibleBottomSheetProps } from './CollapsibleBottomSheet.types';

// TO-DO : hasty implementation, merge with base BottomSheet with proper implementation

const CollapsibleBottomSheet = ({
  children,
  isExpanded: externalIsExpanded,
  onExpandedChange,
  onCollapse,
  initialHeightPercent = INITIAL_HEIGHT_PERCENTAGE,
  className = '',
  showHandle = true,
  enableDrag = true,
  maxHeight,
  initialHeightPx,
  ...additionalProps
}: CollapsibleBottomSheetProps): JSX.Element => {
  const [internalIsExpanded, setInternalIsExpanded] = useState<boolean>(false);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [isAtTop, setIsAtTop] = useState<boolean>(true);
  const [screenHeight, setScreenHeight] = useState<number>(window.innerHeight);
  const [isCollapsed, setIsCollapsed] = useState<boolean>(true); // Add collapsed state
  const contentRef = useRef<HTMLDivElement>(null);
  const resizeTimeout = useRef<NodeJS.Timeout | null>(null);

  // Determine which state to use (external or internal)
  const isControlled = externalIsExpanded !== undefined;
  const isExpanded = isControlled ? externalIsExpanded : internalIsExpanded;
  const setIsExpanded = useCallback(
    (expanded: boolean) => {
      if (isControlled) onExpandedChange?.(expanded);
      else setInternalIsExpanded(expanded);
    },
    [isControlled, onExpandedChange]
  );

  useEffect(() => {
    onExpandedChange?.(isExpanded);
  }, [isExpanded, onExpandedChange]);

  useEffect(() => {
    setInternalIsExpanded(externalIsExpanded || false); // Ensure it's never undefined
  }, [externalIsExpanded]);

  // Heights calculations
  const fullHeight = (screenHeight * FULL_HEIGHT_PERCENTAGE) / 100;

  const initialSheetHeight = initialHeightPx
    ? initialHeightPx
    : (screenHeight * initialHeightPercent) / 100;

  let expandedTargetY = screenHeight - fullHeight;

  // If maxHeight is provided, use it to calculate a new target,
  // but only if it's smaller than the default expanded height.
  if (maxHeight && screenHeight - maxHeight > expandedTargetY) {
    expandedTargetY = screenHeight - maxHeight;
  }

  const initialCollapsedTargetY = screenHeight - initialSheetHeight;
  // This is the offscreen position, used as the starting point for the open animation
  const offScreenY = screenHeight;

  // Initialize spring: Start fully off-screen (hidden)
  const [spring, api] = useSpring(() => ({
    from: { transform: `translateY(${offScreenY}px)` },
    config: SPRING_CONFIG,
    onRest: () => {
      // Update collapsed state when animation completes
      const currentY = parseFloat(
        spring.transform.get().match(/translateY\(([-\d.]+)px\)/)?.[1] || '0'
      );
      const isNowCollapsed = currentY >= offScreenY - 10; // Add small threshold for floating point precision
      setIsCollapsed(isNowCollapsed);
    },
  }));

  const handleResize = useCallback(() => {
    if (resizeTimeout.current) clearTimeout(resizeTimeout.current);
    resizeTimeout.current = setTimeout(() => {
      const newScreenHeight = window.innerHeight;
      setScreenHeight(newScreenHeight);

      const newInitialSheetHeight = initialHeightPx
        ? initialHeightPx
        : (newScreenHeight * initialHeightPercent) / 100;
      const newExpandedSheetHeight =
        maxHeight ?? (newScreenHeight * FULL_HEIGHT_PERCENTAGE) / 100;

      const newExpandedY = newScreenHeight - newExpandedSheetHeight;
      const newCollapsedY = newScreenHeight - newInitialSheetHeight;

      api.start({
        transform: `translateY(${isExpanded ? newExpandedY : newCollapsedY}px)`,
        immediate: false,
      });
    }, 200);
  }, [api, isExpanded, initialHeightPercent, initialHeightPx, maxHeight]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  // Effect to animate the sheet when `isExpanded` changes or when component mounts
  useEffect(() => {
    // If screenHeight is 0
    // we should defer the animation until screenHeight is properly set.
    if (screenHeight === 0) {
      return;
    }
    const targetY = isExpanded ? expandedTargetY : initialCollapsedTargetY;

    const currentSpringY = parseFloat(
      spring.transform.get().match(/translateY\(([-\d.]+)px\)/)?.[1] || '0'
    );

    // Only animate if the target is different from the current visual position
    // or if the component just mounted to its initial open state.
    if (currentSpringY === offScreenY && isExpanded) {
      // This is the first time it's opened; animate from offScreenY to expandedTargetY
      api.start({ transform: `translateY(${expandedTargetY}px)` });
      setIsCollapsed(false);
    } else {
      // For subsequent state changes (expand/collapse)
      api.start({ transform: `translateY(${targetY}px)` });
      setIsCollapsed(false);
    }
  }, [
    isExpanded,
    expandedTargetY,
    initialCollapsedTargetY,
    api,
    screenHeight,
    offScreenY,
    spring.transform,
  ]);

  // Allows dragging the sheet down from any position when using the handle
  const bindHandle = useDrag(
    ({ movement: [, my], down, velocity, direction }) => {
      if (!enableDrag) return;

      const currentY = parseFloat(
        spring.transform.get().match(/translateY\(([-\d.]+)px\)/)?.[1] || '0'
      );
      let newY = currentY + my;

      if (newY < expandedTargetY)
        newY = expandedTargetY + my * DRAG_CONFIG.yNormalisation;
      if (newY > offScreenY) newY = offScreenY; // Allow dragging to offscreen

      if (!down) {
        const velocityThreshold = DRAG_CONFIG.velocityThreshold;
        const distanceThreshold = DRAG_CONFIG.distanceThreshold;

        if (
          (velocity[1] > velocityThreshold || direction[1] > 0) &&
          my > distanceThreshold
        ) {
          // Check if we should collapse completely
          if (
            currentY >= initialCollapsedTargetY &&
            my > distanceThreshold * 2
          ) {
            newY = offScreenY; // Collapse completely
            setIsCollapsed(true);
            // Call onCollapse immediately when we decide to collapse
            onCollapse?.();
          } else {
            newY = initialCollapsedTargetY; // Go to initial height
            setIsExpanded(false);
          }
        } else {
          newY = expandedTargetY;
          setIsExpanded(true);
        }
      }

      api.start({ transform: `translateY(${newY}px)`, immediate: down });
    },
    { filterTaps: true }
  );

  /**
   * Drag binding for the main container
   * Updated to allow collapsing from initial height
   */
  const bind = useDrag(
    ({ movement: [mx, my], down, velocity, direction, event, cancel }) => {
      if (!enableDrag) return;

      if (Math.abs(my) < TAP_THRESHOLD && !down) {
        cancel?.();
        return;
      }
      const isHorizontalGesture = Math.abs(mx) > Math.abs(my);
      if (isHorizontalGesture) return;

      const currentY = parseFloat(
        spring.transform.get().match(/translateY\(([-\d.]+)px\)/)?.[1] || '0'
      );

      // Updated drag conditions to allow collapsing from initial height
      if (
        (!isExpanded && direction[1] < 0) || // Allow dragging up from collapsed
        (isExpanded &&
          direction[1] > 0 &&
          contentRef.current &&
          contentRef.current.scrollTop === 0) || // Allow dragging down from expanded
        (!isExpanded && direction[1] > 0 && currentY >= initialCollapsedTargetY) // Allow dragging down from initial height to collapse
      ) {
        setIsDragging(down);
      } else {
        setIsDragging(false);
      }

      const target = event.target as HTMLElement;
      if (target.closest('.no-drag')) return;
      if (isExpanded && !isAtTop && direction[1] < 0) return; // Don't interfere with scrolling up

      let newY = currentY + my;

      if (newY < expandedTargetY)
        newY = expandedTargetY + my * DRAG_CONFIG.yNormalisation;
      if (newY > offScreenY) newY = offScreenY; // Allow dragging to offscreen

      if (!down) {
        const velocityThreshold = DRAG_CONFIG.velocityThreshold;
        const distanceThreshold = DRAG_CONFIG.distanceThreshold;

        if (
          (velocity[1] > velocityThreshold || direction[1] > 0) &&
          my > distanceThreshold
        ) {
          // Check if we should collapse completely
          if (
            currentY >= initialCollapsedTargetY &&
            my > distanceThreshold * 2
          ) {
            newY = offScreenY; // Collapse completely
            setIsCollapsed(true);
            // Call onCollapse immediately when we decide to collapse
            onCollapse?.();
          } else {
            newY = initialCollapsedTargetY; // Go to initial height
            setIsExpanded(false);
          }
        } else {
          newY = expandedTargetY;
          setIsExpanded(true);
        }
      }

      api.start({ transform: `translateY(${newY}px)`, immediate: down });
    },
    { filterTaps: true }
  );

  const handleScroll = useCallback(() => {
    if (contentRef.current) {
      const { scrollTop } = contentRef.current;
      const isAtTopNow = scrollTop === 0;
      setIsAtTop(isAtTopNow);
    }
  }, []);

  useEffect(() => {
    const contentEl = contentRef.current;
    if (!contentEl) return;

    contentEl.style.overflowY = isDragging ? 'hidden' : 'auto';
  }, [isDragging]);

  // Don't render if completely collapsed
  if (isCollapsed) {
    return <></>;
  }

  return (
    <animated.div
      {...(enableDrag ? bind() : {})}
      {...additionalProps}
      style={spring}
      className={`${styles.container} ${className}`}
    >
      {showHandle && (
        <div
          {...(enableDrag ? bindHandle() : {})}
          className={styles.dragHandleContainer}
        >
          <div
            {...(enableDrag ? bindHandle() : {})}
            className={styles.dragHandle}
          />
        </div>
      )}
      <div className={styles.content} ref={contentRef} onScroll={handleScroll}>
        {children}
      </div>
    </animated.div>
  );
};

export default CollapsibleBottomSheet;
